// The engine on the board page: the evaluation of the position on screen.
//
// The engine itself — the worker, the queue, rating a move, playing one — is a service now
// (services/engine.ts), with no DOM in it, so the tools that are not this page can use it too.
// What is left here is what this page does with it.

import { store, game } from "../state/index.ts";
import { live } from "../state/live.ts";
import { clamp, formatScore, extractEngineScore } from "./utils.js";
import * as config from "./config.js";
import * as engine from "../services/engine.ts";

// The builds, for whoever names the one that is running. The table itself is the service's.
export { ENGINE_BUILDS } from "../services/engine.ts";

// What the engine answers, when it is asked by this page and not by the practice
let displayHandle = null;
let currentDisplayKey = null;

let onEngineScoreChanged = null;

export function initEngine(elements, callbacks = {}) {
    onEngineScoreChanged = callbacks.onEngineScoreChanged;
}

// ---------------------------------------------------------------------------------------------
// The evaluation on screen
// ---------------------------------------------------------------------------------------------

function isLatestEvaluation(id) {
    return id === store.get('engine.evalGeneration');
}

function nextEvaluation() {
    const next = store.get('engine.evalGeneration') + 1;
    store.set('engine.evalGeneration', next);
    return next;
}

function publishScore(info, fen) {
    const score = extractEngineScore(info, fen);
    store.batch((set) => {
        set('engine.latestScoreText', formatScore(info, fen));
        set('engine.latestScorePawns', score);
    });
    if (onEngineScoreChanged) onEngineScoreChanged(score);
}

function clearLines() {
    store.set('engine.pvLines', {});
}

/** What the analysis card shows about the position on the board. */
export async function evaluatePosition() {
    if (!config.isEngineEnabled()) {
        displayHandle?.cancel();
        displayHandle = null;
        currentDisplayKey = null;
        store.batch((set) => {
            set('engine.latestScorePawns', null);
            set('engine.isEnabled', false);
            set('engine.latestDepth', null);
        });
        clearLines();
        return;
    }

    const source = config.getEngineSource();
    const fen = game.fen();
    const maxDepth = clamp(1, 99, config.getEngineDepth());
    const showLines = store.get('engine.showLines');
    const configMultiPV = config.getEngineMultiPV();
    const multiPV = Math.max(1, showLines ? Math.max(4, configMultiPV) : clamp(0, 5, configMultiPV));

    // A move triggers this from several places (board sync, book load, position orchestrator);
    // restarting the very same search each time only throws work away.
    const displayKey = `${source}|${fen}|${maxDepth}|${multiPV}`;
    if (displayHandle && displayKey === currentDisplayKey) return;
    currentDisplayKey = displayKey;
    const evaluationId = nextEvaluation();

    store.batch((set) => {
        set('engine.latestScorePawns', null);
        set('engine.isEnabled', true);
        set('engine.latestDepth', null);
        set('engine.error', null);
    });
    clearLines();

    try {
        await engine.ensure(source);
        if (!isLatestEvaluation(evaluationId)) return;

        // Lowest priority and resumable: rating a move interrupts it, then it carries on.
        displayHandle = engine.analyse({
            fen, depth: maxDepth, multiPV, priority: "display", group: "display", resumable: true,
            onInfo: (info, allLines) => {
                if (!isLatestEvaluation(evaluationId)) return;
                if (info.multipv === 1) {
                    publishScore(info, fen);
                    store.set('engine.latestDepth', info.depth);
                }
                store.set('engine.pvLines', { ...allLines });
            },
        });
        await displayHandle.promise;
    } catch (err) {
        if (!isLatestEvaluation(evaluationId)) return;
        store.set('engine.error', err.message || String(err));
    }
}

export function disableEngine() {
    store.set('engine.latestScorePawns', null);
    displayHandle?.cancel();
    displayHandle = null;
    currentDisplayKey = null;
}

export function enableEngine() {
    evaluatePosition();
}

/** The current evaluation in pawns, or null when there is none. */
export function getCurrentEvalPawns() {
    return store.get('engine.latestScorePawns');
}

// ---------------------------------------------------------------------------------------------
// What the practice asks of the engine, through the service
// ---------------------------------------------------------------------------------------------

export const warmUp = engine.warmUp;
export const rateMove = engine.rateMove;
export const prefetchPosition = engine.prefetchPosition;
export const disposeBrowserEngine = engine.dispose;
